import _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import Button from '../../generic/Button';
import Icon, { icons, IconText } from '../../generic/Icon';
import { Menu, MenuItem } from '../../generic/Menu';
import { useReportType } from '../hooks/useReportType';
import type { ReportsV3QueryParams, ReportV3Format } from '../types';

import styles from './GenerateReportButton.module.less';

type GenerateReportAction = 'generateLink' | 'download' | 'email';

type Props = {
  /**
   * Whether the report is too large to download.
   */
   isTooLargeToDownload: boolean;
 /**
  * Whether to show the "Email" option.
  */
  shouldShowEmail: boolean;
  /**
   * Callback to be called when an item is clicked.
   */
  onItemClick: (action: GenerateReportAction) => void;
  /**
   * The public URL for the report, if it has been generated.
   */
  publicURL?: string;
};

/**
 * Button + dropdown menu which allows the user to generate the report.
 * The dropdown items will eventually change based on the report type and format.
 */
const GenerateReportButton: VoidFunctionComponent<Props> = ({
  isTooLargeToDownload,
  shouldShowEmail,
  onItemClick,
  publicURL,
}) => (
  <Menu
    menuButton={
      <Button type="primary" size="medium" onClick={_.noop} isFullWidth>
        <IconText icon={icons.chevronDown} text="Generate Report" alignIcon="right" />
      </Button>
    }
    onItemClick={(event) => onItemClick(event.value as GenerateReportAction)}
    direction="bottom"
    align="end"
  >
    {!isTooLargeToDownload && publicURL && (
      <MenuItem className={styles.menuItem} value="public">
        <Icon icon={icons.copy} /> Copy Public Link
      </MenuItem>
    )}
    {!isTooLargeToDownload && !publicURL && (
      <MenuItem className={styles.menuItem} value="generateLink">
        <Icon icon={icons.link} /> Generate Public Link
      </MenuItem>
    )}
    {shouldShowEmail ? (
      <MenuItem className={styles.menuItem} value="email">
        <Icon icon={icons.envelope} /> Send to Email
      </MenuItem>
    ) : (
      <MenuItem className={styles.menuItem} value="download">
        <Icon icon={icons.download} /> Download Report
      </MenuItem>
    )}
  </Menu>
);

// We always email PDF reports, because generating + downloading them would be too slow.
// Note that the exception to this rule is with Remediation Guidance reports, because they are typically smaller and can be downloaded quickly.
const alwaysEmailFormat: ReportV3Format = 'PDF';

/**
 * Container component which manages the state for the GenerateReport button.
 */
export const GenerateReportButtonContainer = () => {
  const reportType = useReportType();
  const [query] = useQuery<ReportsV3QueryParams>();

  const reportFormat = query.format;

  // TODO: later we will implement the logic for "if the project/release group is too chonky, they can't download it".
  const isTooLargeToDownload = false;
  const isDownloadingPDFsAllowed = reportType === 'remediationGuidance';
  const shouldShowEmail = isTooLargeToDownload || (reportFormat === alwaysEmailFormat && !isDownloadingPDFsAllowed);

  const onItemClick = (action: GenerateReportAction) => {
    // eslint-disable-next-line no-console
    console.log({ action });
  };

  return (
    <GenerateReportButton
      isTooLargeToDownload={isTooLargeToDownload}
      shouldShowEmail={shouldShowEmail}
      onItemClick={onItemClick}
    />
  );
};
